/**
 * Unit + structure rendering.
 *
 * Sprites are pooled per entity id. Positions are interpolated between the last
 * two server snapshots (the server ticks at 10 Hz, the client renders at 60).
 * Each entity draws two sprites: an untinted detail layer and a mask layer
 * tinted with the owner's kingdom colour.
 */

import { Container, Graphics, Sprite, Text } from 'pixi.js';
import {
  ART, MAP_H, MAP_W, Phase, PING_COLOR, PlayerKind, RACE_DEFS, Race, RACE_POWERS, RACE_POWER_RANGE,
  SPAWN_RADIUS,
  STRUCTURE_DEFS, StructureType, UNIT_DEFS, UNIT_INFLUENCE_RANGE, UnitType,
} from '../../shared/constants';
import { shortNumber } from '../../shared/util';
import { anchorFor } from '../anchors';
import { atWarWith, isFriendly, state } from '../state';

/**
 * How far in you have to be before buildings say their own names.
 *
 * Below this a tile is a handful of pixels and the labels would be a grey
 * smear over the whole province; above it they are the difference between a
 * skyline and information.
 */
const STRUCT_LABEL_ZOOM = 0.8;
import { structSprites, unitSprites, warshipSprites } from './sprites';

/**
 * The drawing for one unit on the map.
 *
 * Almost always just its type. The **warship** is the exception: every people
 * sails the same ship with the same numbers, so they share a `UnitType` and
 * differ only in the hull — which means the answer depends on who owns this
 * one. A fleet on the horizon should say whose it is before you can read a
 * health bar. Falls back to the type's own sprite when the owner's people are
 * not known, which is better than drawing nothing.
 */
function spritesFor(u: { type: number; owner: number }) {
  if (u.type === UnitType.Warship) {
    const race = state.players.get(u.owner)?.race;
    if (race !== undefined && warshipSprites[race]) return warshipSprites[race];
  }
  return unitSprites[u.type];
}

interface Pair { base: Sprite; mask: Sprite; }

/**
 * A frontline as drawn, easing towards where the server says it is.
 *
 * The raw value moves in jumps: the marker is an average over the tiles taken,
 * it only reaches us five times a second, and during a real war it can shift
 * several tiles between updates. Read straight, the number on a contested border
 * flickers and skitters and is unreadable exactly when it matters most. Easing
 * it into place costs nothing and makes it a label you can actually read.
 */
interface SmoothFront {
  x: number; y: number; heading: number;
  atkTroops: number; defTroops: number;
  attacker: number; defender: number;
  seen: number;
}

const FRONT_LERP = 0.10;

/**
 * **How much bigger a kingdom's name gets as it grows.**
 *
 * Logarithmic, because territory spans three orders of magnitude in one match —
 * a survivor clinging to sixty tiles and an empire holding sixty thousand are
 * both on screen at once, and anything linear would render the first as dust
 * and the second as a billboard across the continent.
 *
 * `1` is the floor and it is exactly the size every label used to be, so
 * nothing on the map has become harder to read: the smallest realms look the
 * same as before and everything above them grows away from that. The ceiling is
 * two and a half times, reached around fifteen thousand tiles — past that the
 * leader is obvious from the colour of the map anyway, and letting it keep
 * growing would only bury its neighbours' names underneath it.
 */
const LABEL_FLOOR_TILES = 60;

/**
 * **How big a kingdom's name is, in three numbers.**
 *
 * `BASE` is what the smallest named realm gets, and it is below one on purpose:
 * the type is rasterised at eleven pixels, and eleven pixels turned out to be
 * shouting next to the sixty tiles it was labelling. `GROWTH_PER_DOUBLING` is
 * how much bigger the name gets each time the realm doubles, and `MAX_GROWTH`
 * is where that stops.
 *
 * The spread used to be 1.0 → 2.5. That made the largest name two and a half
 * times the smallest, which read well with a dozen kingdoms on screen and
 * became a wall of text with a hundred: at map scale a name is the same size
 * whatever the zoom (see the `/ zoom` below), so zooming out does not shrink
 * the labels, it only packs more of them into the same screen. Smaller
 * throughout, and a gentler curve, is what makes a crowded continent legible.
 */
const LABEL_BASE = 0.72;
const LABEL_MAX_GROWTH = 1.6;
const LABEL_GROWTH_PER_DOUBLING = 0.14;

/**
 * How wide a kingdom's main blob must be **on screen** before it is named, and
 * over how many further pixels the name fades up to full.
 *
 * This is what makes a small realm something you have to zoom in on, and it is
 * the main defence against a crowded map: because a label is a constant size on
 * screen whatever the zoom, pulling back does not shrink the names, it only
 * fits more of them in. Raising the bar is what thins them out.
 *
 * At the default zoom a sixty-tile holding is about sixty pixels across and
 * sits just under the line, so it wants a nudge closer; a ten-tile rump needs
 * roughly three times the magnification. The rule before all this was a flat
 * `count > 40` at every zoom, which at map scale wrote a hundred names over a
 * continent and at close range still hid the neighbour you were fighting.
 */
const LABEL_MIN_SPAN_PX = 64;
const LABEL_FADE_PX = 28;

/**
 * Ceiling on named kingdoms, for the frame budget rather than for looks — the
 * zoom rule above is what usually decides. Well above the twenty-six it used to
 * be, because that cap was low enough to bite during a normal match and a label
 * dropping in and out at the boundary is its own kind of flicker.
 */
const MAX_KINGDOM_LABELS = 48;

function labelScale(tiles: number): number {
  if (tiles <= LABEL_FLOOR_TILES) return LABEL_BASE;
  const doublings = Math.log2(tiles / LABEL_FLOOR_TILES);
  return Math.min(LABEL_MAX_GROWTH, LABEL_BASE + LABEL_GROWTH_PER_DOUBLING * doublings);
}

/**
 * **Zooming in on something makes its name readable.**
 *
 * A label is scaled by `1 / zoom`, which pins it to a constant size on screen —
 * so a small realm's name was the same eight pixels however far you flew into
 * it. That is exactly backwards from why anybody zooms in on a small kingdom or
 * a monster camp: to read the thing the map is too crowded to show.
 *
 * So the *floor* rises with the zoom. Pulled back, a minor holding keeps the
 * small name it needs to have on a hundred-kingdom continent; pushed in past
 * `LIFT_TO`, every label is at least `READABLE` whatever it owns. Realms
 * already bigger than the floor are untouched — this only ever lifts, never
 * shrinks.
 */
const LABEL_READABLE = 1.35;
const LABEL_LIFT_FROM_ZOOM = 1.0;
const LABEL_LIFT_TO_ZOOM = 2.2;

function labelSize(tileGrow: number, zoom: number): number {
  const span = LABEL_LIFT_TO_ZOOM - LABEL_LIFT_FROM_ZOOM;
  const lift = Math.max(0, Math.min(1, (zoom - LABEL_LIFT_FROM_ZOOM) / span));
  return Math.max(tileGrow, LABEL_BASE + (LABEL_READABLE - LABEL_BASE) * lift);
}

/**
 * A kingdom's own colour, lifted toward white until it is readable as text.
 *
 * The name is drawn **on that kingdom's own ground**, in that kingdom's colour,
 * which is exactly the situation where a colour disappears into itself. Mixing
 * a third of the way to white keeps the banner recognisable — you can still
 * tell crimson from rose at a glance — while separating the glyphs from the
 * territory fill underneath, and it rescues the dark end of the palette, which
 * against the near-black outline would otherwise be a silhouette.
 */
function labelTint(color: number): number {
  const r = (color >> 16) & 255, g = (color >> 8) & 255, b = color & 255;
  const lift = (c: number) => Math.round(c + (255 - c) * 0.34);
  return (lift(r) << 16) | (lift(g) << 8) | lift(b);
}

export class EntityLayer {
  readonly structures = new Container();
  readonly units = new Container();
  readonly overlay = new Graphics();
  readonly labels = new Container();

  private unitPairs = new Map<number, Pair>();
  private structPairs = new Map<number, Pair>();
  /** keyed by player id — see `drawLabels` for why that is not an optimisation */
  private labelPool = new Map<number, Text>();
  /** each label's size multiplier, so the between-frames rescale can keep it */
  private labelGrow = new Map<number, number>();
  private structLabelPool: Text[] = [];
  /**
   * The footprint the armed race power would actually hit.
   *
   * Its own Graphics, rebuilt only when the aim, the ring or the world changes
   * — a thousand tiles of fill rebuilt sixty times a second would cost more
   * than the rest of the frame put together, and the answer does not change
   * between mouse moves.
   */
  private powerPreview = new Graphics();
  private previewKey = '';
  private frontPool: Text[] = [];
  private lastLabelAt = 0;
  private fronts = new Map<number, SmoothFront>();
  private frontStamp = 0;

  constructor() {
    this.structures.eventMode = 'none';
    this.units.eventMode = 'none';
    this.overlay.eventMode = 'none';
    this.labels.eventMode = 'none';
    this.powerPreview.eventMode = 'none';
    // under the labels, over the ground: it is a wash on the map, not furniture
    this.labels.addChildAt(this.powerPreview, 0);
  }

  update(now: number, zoom: number) {
    this.drawStructures();
    this.drawUnits(now);
    this.easeFronts();
    this.drawOverlay(zoom);
    // kingdom names are expensive and change slowly; the frontline numbers glide
    // every frame or the easing above would be pointless
    if (now - this.lastLabelAt > 350) {
      this.lastLabelAt = now;
      this.drawLabels(zoom);
      this.drawStructLabels(zoom);
    } else {
      /*
       * **Keep each kingdom's own size while only the zoom is being tracked.**
       *
       * This line is where the flickering actually came from. Labels are rebuilt
       * at about 3 Hz and merely rescaled on the twenty frames in between — and
       * the rescale used to write `1 / zoom`, throwing away the `grow` factor
       * that makes a big realm's name big. So every name was drawn at its true
       * size for one frame and at *floor* size for the next twenty, three times
       * a second, for the whole match. It looked like the sizing could not make
       * its mind up; it was one code path disagreeing with the other.
       */
      for (const [id, t] of this.labelPool) {
        if (t.visible) t.scale.set(labelSize(this.labelGrow.get(id) ?? LABEL_BASE, zoom) / zoom);
      }
      for (const t of this.structLabelPool) if (t.visible) t.scale.set(1 / zoom);
    }
    this.drawFronts(zoom);
    // cheap when nothing has moved: it compares one key and returns
    this.drawPowerPreview();
  }

  /**
   * **What the armed power would actually hit, before you commit to it.**
   *
   * Not the ring — the *tiles*. The blast only ever falls on ground the target
   * kingdom holds, so the shape you see is their border clipped to your ring,
   * and the answer to "does this reach their capital or stop at the river" is
   * on the screen instead of in your head. Thirty-six thousand of each resource
   * is far too much to spend on a guess.
   *
   * Rebuilt only when the aim, the ring or the world changes — the key catches
   * all three, and between mouse moves this costs a string comparison.
   */
  private drawPowerPreview() {
    const g = this.powerPreview;
    const armed = state.powerMode && state.phase === Phase.Live && state.hoverTile >= 0;
    // the time bucket is there so a border shifting under the cursor refreshes
    // the footprint a few times a second, without rebuilding it every frame
    const key = armed
      ? `${state.hoverTile}:${state.powerRadius}:${Math.floor(performance.now() / 250)}`
      : '';
    if (key === this.previewKey) return;
    this.previewKey = key;
    g.clear();
    if (!armed) return;

    const hx = (state.hoverTile % MAP_W) + 0.5;
    const hy = ((state.hoverTile / MAP_W) | 0) + 0.5;
    const realm = state.owner[state.hoverTile];
    const r = state.powerRadius;

    /*
     * **Out of reach is drawn, not discovered.**
     *
     * `cmdPower` measures `RACE_POWER_RANGE` from your realm's centroid and
     * silently refuses anything beyond it. Aiming a doomsday weapon at a
     * province across the map and getting no strike, no explosion and no
     * explanation reads as a broken button — so the ring goes red the moment
     * the cursor leaves your reach, and `input.ts` will not even send the click.
     */
    const dx = hx - state.self.centroidX, dy = hy - state.self.centroidY;
    const tooFar = dx * dx + dy * dy > RACE_POWER_RANGE * RACE_POWER_RANGE;

    // the ring you set, always — even over your own ground, where it will do
    // nothing, because seeing that it would do nothing is the useful part
    g.circle(hx * ART, hy * ART, r * ART)
      .stroke({ width: 2, color: tooFar ? 0xff4444 : 0xff9d5c, alpha: 0.9 });

    if (tooFar) {
      // the edge of what you can reach, so the answer is "walk it closer", not
      // "the button is broken"
      g.circle(state.self.centroidX * ART, state.self.centroidY * ART, RACE_POWER_RANGE * ART)
        .stroke({ width: 2, color: 0xff4444, alpha: 0.35 });
      return;
    }

    if (realm === 0 || realm === state.you || isFriendly(realm)) return;

    /*
     * The footprint: their tiles inside the ring, **merged into horizontal
     * runs**.
     *
     * The ring reaches a hundred and fifty tiles, which is seventy thousand
     * squares — one rectangle each would put seventy thousand shapes into the
     * geometry four times a second and stop the frame dead. A realm's ground is
     * contiguous, so a row of three hundred tiles collapses into one or two
     * rectangles and the whole footprint costs a few hundred instead. Same
     * picture, three orders of magnitude cheaper.
     */
    const rr = Math.ceil(r);
    const cx = state.hoverTile % MAP_W, cy = (state.hoverTile / MAP_W) | 0;
    let runs = 0;
    for (let dy = -rr; dy <= rr; dy++) {
      const ny = cy + dy;
      if (ny < 0 || ny >= MAP_H) continue;
      // how far the circle reaches on this row, so the scan is a disc not a box
      const half = Math.floor(Math.sqrt(Math.max(0, r * r - dy * dy)));
      const from = Math.max(0, cx - half), to = Math.min(MAP_W - 1, cx + half);
      const row = ny * MAP_W;
      let start = -1;
      for (let nx = from; nx <= to; nx++) {
        const mine = state.owner[row + nx] === realm;
        if (mine && start < 0) start = nx;
        if (!mine && start >= 0) {
          g.rect(start * ART, ny * ART, (nx - start) * ART, ART);
          runs++;
          start = -1;
        }
      }
      if (start >= 0) { g.rect(start * ART, ny * ART, (to + 1 - start) * ART, ART); runs++; }
    }
    if (runs > 0) g.fill({ color: 0xff6b35, alpha: 0.3 });
  }

  /** Pulls every drawn frontline a little closer to where the server put it. */
  private easeFronts() {
    const stamp = ++this.frontStamp;
    for (const f of state.fronts) {
      const key = f.attacker * 65536 + f.defender;
      const s = this.fronts.get(key);
      if (!s) {
        this.fronts.set(key, { ...f, seen: stamp });
        continue;
      }
      s.x += (f.x - s.x) * FRONT_LERP;
      s.y += (f.y - s.y) * FRONT_LERP;
      s.atkTroops += (f.atkTroops - s.atkTroops) * FRONT_LERP;
      s.defTroops += (f.defTroops - s.defTroops) * FRONT_LERP;
      // rotate the short way round, or the arrow spins through half a circle
      let d = f.heading - s.heading;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      s.heading += d * FRONT_LERP;
      s.seen = stamp;
    }
    for (const [key, s] of this.fronts) if (s.seen !== stamp) this.fronts.delete(key);
  }

  private drawStructures() {
    for (const [id, pair] of this.structPairs) {
      if (!state.structures.has(id)) {
        pair.base.destroy();
        pair.mask.destroy();
        this.structPairs.delete(id);
      }
    }
    for (const s of state.structures.values()) {
      let pair = this.structPairs.get(s.id);
      const owner = state.players.get(s.owner);
      // bastions are gone: nothing stands on a wall, so a tower is always a
      // tower. See `addStructure` and `canWall` on the server.
      const set = structSprites[s.type];
      if (!set) continue;
      if (!pair) {
        const base = new Sprite(set.base[0]);
        const mask = new Sprite(set.mask[0]);
        base.anchor.set(0.5, 0.62);
        mask.anchor.set(0.5, 0.62);
        this.structures.addChild(base);
        this.structures.addChild(mask);
        pair = { base, mask };
        this.structPairs.set(s.id, pair);
      }
      const x = ((s.tile % MAP_W) + 0.5) * ART;
      const y = (((s.tile / MAP_W) | 0) + 0.5) * ART;
      pair.base.texture = set.base[0];
      pair.mask.texture = set.mask[0];
      pair.mask.tint = owner ? owner.color : 0xffffff;

      /*
       * A building under construction rises out of the ground as it is built:
       * squashed and translucent at first, full height when the roof is on.
       */
      const raising = s.built < 1;
      const grow = raising ? 0.35 + s.built * 0.65 : 1;
      pair.base.scale.y = grow;
      pair.mask.scale.y = grow;
      pair.base.position.set(x, y);
      pair.mask.position.set(x, y);
      const hurt = s.hp < 200;
      pair.base.alpha = raising ? 0.45 + s.built * 0.5 : (hurt ? 0.75 : 1);
      pair.mask.alpha = raising ? 0.45 + s.built * 0.5 : 1;
    }
  }

  private drawUnits(now: number) {
    for (const [id, pair] of this.unitPairs) {
      if (!state.units.has(id)) {
        pair.base.destroy();
        pair.mask.destroy();
        this.unitPairs.delete(id);
      }
    }

    for (const u of state.units.values()) {
      const set = spritesFor(u);
      if (!set) continue;
      let pair = this.unitPairs.get(u.id);
      if (!pair) {
        const base = new Sprite(set.base[0]);
        const mask = new Sprite(set.mask[0]);
        base.anchor.set(0.5, 0.75);
        mask.anchor.set(0.5, 0.75);
        this.units.addChild(base);
        this.units.addChild(mask);
        pair = { base, mask };
        this.unitPairs.set(u.id, pair);
      }

      const t = Math.min(1.35, (now - u.t0) / state.tickInterval);
      const x = (u.px + (u.tx - u.px) * t) * ART;
      const y = (u.py + (u.ty - u.py) * t) * ART;

      const moving = Math.abs(u.tx - u.px) + Math.abs(u.ty - u.py) > 0.01;
      const frame = moving ? (((now / 140) | 0) + u.id) % 2 : 0;
      const bob = moving ? 0 : ((((now / 420) | 0) + u.id) % 2);

      pair.base.texture = set.base[frame];
      pair.mask.texture = set.mask[frame];
      pair.base.position.set(x, y - bob);
      pair.mask.position.set(x, y - bob);

      const flip = (u.flags & 1) !== 0;
      pair.base.scale.x = flip ? -1 : 1;
      pair.mask.scale.x = flip ? -1 : 1;

      const owner = state.players.get(u.owner);
      pair.mask.tint = owner ? owner.color : 0xffffff;
      const attacking = (u.flags & 2) !== 0;
      pair.base.tint = attacking ? 0xffdcc0 : 0xffffff;

      /*
       * Nothing on the map is translucent any more.
       *
       * This used to dim a sneaking thief to 0.62 and flush it red once a tower
       * caught it, reading bits 4 and 16 of the flag byte. Thieves are gone and
       * both bits went with them — deliberately left as holes in
       * `shared/constants.ts` rather than closed up — so the branch has been
       * drawing every unit at full opacity for some time. It is removed rather
       * than left as harmless dead code precisely *because* it is harmless
       * today: the day somebody spends bit 4 on a new flag, this would quietly
       * make whichever units carry it half-invisible, and the bug would look
       * like a rendering fault rather than a stale constant.
       */
      pair.base.alpha = 1;
      pair.mask.alpha = 1;
    }
  }

  private drawOverlay(zoom: number) {
    const g = this.overlay;
    g.clear();
    const px = 1 / zoom;

    /*
     * **Crossed swords over every kingdom you are at war with.**
     *
     * This stopped being decoration the moment soldiers began refusing to fight
     * anybody you have not declared on: whether there is a war between you and
     * that banner is now the difference between an army that will engage and an
     * army that will walk past. It has to be answerable from the map, at a
     * glance, without hovering anything.
     *
     * **On the name line, at the end of it** — right after `(Humans)` — rather
     * than floating above the label at the realm's centroid. Two changes in
     * one, and both were wrong the same way: the old mark sat at `sumX/count`,
     * which is the average of a ring rather than anywhere the realm stands, so
     * on a donut-shaped empire it hung over somebody else's ground entirely;
     * and a mark *above* a two-line label reads as a separate thing on the map
     * rather than as part of what the label says.
     *
     * And it is drawn as **swords**, not as a cross. Two bare strokes through
     * each other is an X — the universal mark for "no" — which is close to the
     * opposite of what it means. Blades with crossguards and pommels read as
     * war at the same size.
     */
    if (state.phase === Phase.Live) {
      for (const p of state.players.values()) {
        if (!atWarWith(p.id)) continue;
        const label = this.labelPool.get(p.id);
        // only where the name itself is drawn: the mark belongs to the label,
        // so a realm too small to be named at this zoom gets no floating mark
        if (!label || !label.visible) continue;
        const anchor = anchorFor(p.id);
        if (!anchor) continue;

        // just past the right edge of the name, on the name's own line — the
        // label is anchored in its middle and holds two lines of text
        const s = label.scale.x;
        const x = anchor.x * ART + (label.width / 2) + 5 * px * s;
        const y = anchor.y * ART - (label.height / 4);
        const b = 3.4 * px * s;
        const w = Math.max(0.8, px * 1.5 * s);

        /* two blades, north-east and north-west, crossing below their middles */
        const blade = (dir: number) => {
          const tipX = x + b * dir, tipY = y - b;
          const hiltX = x - b * 0.75 * dir, hiltY = y + b;
          return { tipX, tipY, hiltX, hiltY };
        };
        for (const pass of [0, 1]) {
          const dark = pass === 0;
          for (const dir of [1, -1]) {
            const { tipX, tipY, hiltX, hiltY } = blade(dir);
            // the blade
            g.moveTo(hiltX, hiltY).lineTo(tipX, tipY)
              .stroke({
                width: dark ? w * 2.6 : w,
                color: dark ? 0x0a0d14 : 0xf0d9a0,
                alpha: dark ? 0.85 : 0.98,
              });
            if (dark) continue;
            // crossguard across the blade near the hilt, and a pommel behind it
            const gx = (tipX - hiltX) * 0.18, gy = (tipY - hiltY) * 0.18;
            const cx = hiltX + gx, cy = hiltY + gy;
            g.moveTo(cx - gy * 1.6, cy + gx * 1.6).lineTo(cx + gy * 1.6, cy - gx * 1.6)
              .stroke({ width: w * 0.9, color: 0xc9a227, alpha: 0.95 });
            g.circle(hiltX, hiltY, w * 0.8).fill({ color: 0xc9a227, alpha: 0.95 });
          }
        }
      }
    }

    /*
     * **A doomsday weapon, while it is falling: whose, on whom, how wide.**
     *
     * The single loudest thing in the game used to be legible only as craters
     * appearing in silence — you could not tell who had spent thirty-six
     * thousand, on whom, or where the blast stopped. Now the fall zone is a
     * ring in the caster's own colour, and the name of the victim is under it.
     */
    for (const run of state.powers) {
      const caster = state.players.get(run.owner);
      const colour = caster ? caster.color : 0xff6b35;
      const beat = (performance.now() / 500) % 1;
      const x = run.x * ART, y = run.y * ART;
      g.circle(x, y, run.area * ART)
        .stroke({ width: Math.max(1.5, px * 2.6), color: colour, alpha: 0.85 });
      // a pulse running out from the middle, so it reads as *falling* and not
      // as a territory marker somebody left behind
      g.circle(x, y, run.area * ART * beat)
        .stroke({ width: Math.max(1, px * 1.8), color: 0xffd85e, alpha: 0.7 * (1 - beat) });
    }

    /*
     * Who is settling where, while everyone is still choosing.
     *
     * Nobody is committed during the spawn phase — a click moves your kingdom —
     * so the phase is really a silent negotiation over the map, and it only
     * works if you can see what everyone else has claimed so far. A kingdom's
     * disc is already painted in its colour; this pulses a ring around each one
     * so the picture reads at the pulled-back view you are choosing from, with
     * your own drawn brightest.
     */
    if (state.phase === Phase.Spawn) {
      const pulse = (performance.now() / 850) % 1;
      for (const p of state.players.values()) {
        if (p.count <= 0 || p.kind === PlayerKind.Camp) continue;
        const x = (p.sumX / p.count) * ART;
        const y = (p.sumY / p.count) * ART;
        const mine = p.id === state.you;
        g.circle(x, y, (SPAWN_RADIUS + 0.5 + pulse * 4) * ART)
          .stroke({
            width: Math.max(1, px * (mine ? 2.6 : 1.7)),
            color: p.color,
            alpha: (mine ? 0.95 : 0.55) * (1 - pulse),
          });
        g.circle(x, y, ART * 1.6)
          .fill({ color: p.color, alpha: mine ? 0.85 : 0.45 });
      }
    }

    /*
     * The market you have picked up, and where its wagons are going.
     *
     * A trade route is invisible by nature — a decision about a building that
     * looks exactly the same afterwards. Without this the only sign that a
     * market was in hand was a notice that had already faded, and the only way
     * to check where one was selling was to remember. The ring pulses so it
     * reads as "waiting for your next click", and a dashed line runs to the
     * partner once one is fixed.
     */
    if (state.selectedMarket >= 0) {
      const mk = state.structures.get(state.selectedMarket);
      if (mk) {
        const x = (mk.tile % MAP_W) * ART + ART / 2;
        const y = ((mk.tile / MAP_W) | 0) * ART + ART / 2;
        const beat = (performance.now() / 700) % 1;
        g.circle(x, y, ART * (1.4 + beat * 1.6))
          .stroke({ width: Math.max(1, px * 2.2), color: 0xffd85e, alpha: 0.9 * (1 - beat) });
        g.circle(x, y, ART * 1.2)
          .stroke({ width: Math.max(0.8, px * 1.6), color: 0xffd85e, alpha: 0.8 });
      }
    }

    /*
     * The ground your soldiers hold.
     *
     * A unit roughly doubles what its tile costs an attacker, and the effect
     * reaches three tiles — but none of that is visible, so nobody positions for
     * it. A faint disc makes the strongest defensive tool in the game something
     * you can actually see and place. Only your own and your allies' units get
     * one: where an enemy is strong is for you to find out.
     */
    const ringR = UNIT_INFLUENCE_RANGE * ART;
    for (const u of state.units.values()) {
      if (!isFriendly(u.owner)) continue;
      const def = UNIT_DEFS[u.type];
      if (!def || def.defencePower <= 0) continue;      // boats and caravans hold nothing
      const pair = this.unitPairs.get(u.id);
      if (!pair) continue;
      const mine = u.owner === state.you;
      // stronger units get a slightly stronger ring, so the map reads at a glance
      const weight = Math.min(1, def.defencePower / 3);
      g.circle(pair.base.x, pair.base.y, ringR)
        .fill({ color: mine ? 0x7ec878 : 0x7fd4ff, alpha: 0.035 + weight * 0.03 })
        .stroke({ width: Math.max(0.4, px * 0.8), color: mine ? 0x7ec878 : 0x7fd4ff, alpha: 0.16 + weight * 0.12 });
    }

    for (const u of state.units.values()) {
      const set = spritesFor(u);
      if (!set) continue;
      const pair = this.unitPairs.get(u.id);
      if (!pair) continue;
      const x = pair.base.x, y = pair.base.y;

      if (state.selected.has(u.id)) {
        g.circle(x, y - set.h * 0.25, set.w * 0.7)
          .stroke({ width: Math.max(0.6, px), color: 0x9ff2a0, alpha: 0.9 });
      }
      if (u.hp < 250) {
        const w = set.w * 0.9;
        const h = Math.max(1, px * 2);
        const top = y - set.h * 0.85;
        g.rect(x - w / 2, top, w, h).fill({ color: 0x000000, alpha: 0.65 });
        const frac = u.hp / 255;
        g.rect(x - w / 2, top, w * frac, h)
          .fill({ color: frac > 0.5 ? 0x6fd66f : frac > 0.25 ? 0xe8c56a : 0xe06060 });
      }
    }

    for (const s of state.structures.values()) {
      const x = ((s.tile % MAP_W) + 0.5) * ART;
      const y = (((s.tile / MAP_W) | 0) + 0.5) * ART;
      if (s.hp < 250) {
        const w = 8, h = Math.max(1, px * 2);
        g.rect(x - w / 2, y - 8, w, h).fill({ color: 0x000000, alpha: 0.6 });
        g.rect(x - w / 2, y - 8, w * (s.hp / 255), h).fill({ color: 0x7fc9ff });
      }
      /*
       * A progress bar under whichever building is actually training something,
       * so you can see your barracks working without opening a panel.
       */
      // scaffolding first: a building being raised shows how far along it is
      const job = s.built < 1
        ? { type: -1, progress: s.built }
        : (s.jobs && s.jobs.length > 0 ? s.jobs[0] : null);
      if (job) {
        const w = 9, h = Math.max(1.2, px * 2.4), top = y + 7;
        g.rect(x - w / 2 - 0.4, top - 0.4, w + 0.8, h + 0.8).fill({ color: 0x0a0d14, alpha: 0.75 });
        g.rect(x - w / 2, top, w, h).fill({ color: 0x2b2418, alpha: 0.95 });
        g.rect(x - w / 2, top, w * Math.max(0.02, job.progress), h)
          .fill({ color: s.built < 1 ? 0x9fd4ff : 0xffd85e });
        if (s.queue > 1) {
          for (let i = 1; i < Math.min(3, s.queue); i++) {
            g.rect(x + w / 2 + 1 + (i - 1) * 2, top, 1.2, h).fill({ color: 0xffd85e, alpha: 0.5 });
          }
        }
      }
      // stack level pips, openfront style
      if (s.level > 1) {
        for (let i = 0; i < s.level; i++) {
          g.rect(x - s.level + i * 2, y + 5.5, 1.4, 1.6)
            .fill({ color: 0xffe9a8, alpha: 0.95 });
        }
      }
    }

    /*
     * Where each war is going. A pulsing spearhead sits on the front, pointing
     * the way the attack is rolling, drawn in the attacker's own colour — so at
     * a glance you can tell who is pushing into whom, and from which side.
     */
    const beat = (performance.now() / 900) % 1;
    for (const f of this.fronts.values()) {
      const x = f.x * ART, y = f.y * ART;
      const atk = state.players.get(f.attacker);
      const colour = atk ? atk.color : 0xffffff;
      const c = Math.cos(f.heading), s = Math.sin(f.heading);
      const w = Math.max(0.9, px * 1.8);

      // the spearhead, nudged forward on every pulse
      const lead = 5 + beat * 4;
      const tipX = x + c * lead, tipY = y + s * lead;
      const back = 6, side = 4.2;
      g.moveTo(tipX - c * back - s * side, tipY - s * back + c * side)
        .lineTo(tipX, tipY)
        .lineTo(tipX - c * back + s * side, tipY - s * back - c * side)
        .stroke({ width: w, color: colour, alpha: 0.95 - beat * 0.45 });

      // the shaft: a short tail showing where the push is coming from
      g.moveTo(x - c * 9, y - s * 9).lineTo(tipX - c * 2, tipY - s * 2)
        .stroke({ width: w * 0.8, color: colour, alpha: 0.5 - beat * 0.3 });

      // crossed blades between the two frontline numbers
      const b = 2.8, by = y - 9;
      g.moveTo(x - b, by - b).lineTo(x + b, by + b)
        .moveTo(x + b, by - b).lineTo(x - b, by + b)
        .stroke({ width: Math.max(0.7, px * 1.4), color: 0xffe9a8, alpha: 0.95 });
    }

    // team pings
    const now = performance.now();
    for (let i = state.pings.length - 1; i >= 0; i--) {
      const p = state.pings[i];
      const age = (now - p.born) / 1000;
      if (age > 4) { state.pings.splice(i, 1); continue; }
      const r = 6 + (age % 1) * 14;
      const fade = 1 - (age % 1);
      /*
       * Two rings: the outer one says **what** — strike, hold, send — in the
       * kind's own colour, matching the button that dropped it; the inner one
       * says **who**, in the sender's. With no chat and three fixed meanings,
       * the meaning is the half worth reading across a continent, and the
       * sender is the half worth reading up close.
       */
      g.circle(p.x * ART, p.y * ART, r)
        .stroke({ width: Math.max(0.8, px * 1.5), color: PING_COLOR[p.kind] ?? p.color, alpha: fade });
      g.circle(p.x * ART, p.y * ART, r * 0.62)
        .stroke({ width: Math.max(0.6, px * 1.1), color: p.color, alpha: fade * 0.9 });
    }
  }

  /**
   * Kingdom names carry their strength — `name (people)` over `troops / cap` —
   * so you can read the whole balance of power off the map without opening a
   * panel, including *who* you are looking at. Which people a realm is matters
   * now that every race builds differently and trading across the line pays
   * fifty per cent more.
   *
   * **And the name is as big as the realm is.** Every label used to be the same
   * eleven pixels, which made a hundred-tile squatter shout exactly as loudly as
   * the empire about to eat it — so the one thing the map should tell you at a
   * glance, *who is winning*, was the one thing you had to read the numbers to
   * find out. Now the size is the headline and the numbers are the detail.
   */
  private drawLabels(zoom: number) {
    /*
     * **Sorted only to decide who gets a label, never to decide which one.**
     *
     * This is where the flickering came from, and it was not the sizing. The
     * pool used to be indexed by *rank*: `labelPool[0]` belonged to whoever was
     * biggest this frame. Territory moves every tick, so two kingdoms of
     * similar size trade places several times a second — and each swap handed
     * the same `Text` object a different name, a different scale and a
     * different resolution. What you saw as one label flashing between large
     * and small was two labels changing places on top of each other.
     *
     * Keyed by player id, a label belongs to a kingdom for as long as the
     * kingdom exists, and its size only ever changes when *its own* land does.
     */
    const shown = new Set<number>();
    const entries = [...state.players.values()]
      .filter(p => p.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_KINGDOM_LABELS);

    for (const p of entries) {
      const anchor = anchorFor(p.id);
      if (!anchor) continue;           // no sweep has seen it yet; next one will

      /*
       * **Small realms have to be zoomed in on**, which is the honest answer to
       * a hundred kingdoms on one continent: a name is only worth drawing when
       * the ground under it is big enough on *screen* to be worth naming.
       * Measured off the contiguous blob rather than the total, so a realm
       * scattered in fragments does not shout as loudly as one that holds a
       * province.
       */
      const spanPx = Math.sqrt(Math.max(1, anchor.blob)) * ART * zoom;
      if (spanPx < LABEL_MIN_SPAN_PX) continue;

      let label = this.labelPool.get(p.id);
      if (!label) {
        label = new Text({
          text: '',
          style: {
            fontFamily: 'monospace', fontSize: 11, fill: 0xffffff, align: 'center', lineHeight: 12,
            stroke: { color: 0x0a0d14, width: 3 },
          },
        });
        label.anchor.set(0.5);
        label.resolution = 2;
        this.labels.addChild(label);
        this.labelPool.set(p.id, label);
      }
      shown.add(p.id);

      const race = p.kind === PlayerKind.Camp ? '' : ` (${RACE_DEFS[p.race]?.name || '?'})`;
      label.visible = true;
      label.text = `${p.name}${race}\n${shortNumber(p.troops)} / ${shortNumber(p.troopCap)}`;
      // a realm's name in its own banner, so the map reads as colour rather than
      // as a list of white words you have to match against the ground beneath
      label.tint = labelTint(p.color);
      // fade in over the threshold rather than popping into existence
      label.alpha = 0.9 * Math.min(1, (spanPx - LABEL_MIN_SPAN_PX) / LABEL_FADE_PX + 0.15);

      /*
       * The type is rasterised once at eleven pixels and then stretched, so a
       * label blown up to two and a half times would be a blurred one. Asking
       * for more resolution re-renders it at that density instead.
       *
       * **Quantised, and only ever raised.** `ceil` on a continuously moving
       * number re-rasterises the texture every time the realm crosses a
       * boundary, which for a kingdom sitting near one is every few frames.
       * Whole steps that never come back down cost one re-render each and then
       * nothing.
       */
      // the tile-based size is what is remembered; the zoom floor is applied at
      // draw time, in both this path and the rescale below, so a zoom gesture
      // does not wait up to 350 ms for the next rebuild to be honoured
      const grow = labelScale(p.count);
      const size = labelSize(grow, zoom);
      const res = Math.min(6, Math.max(2, Math.round(size * 2)));
      if (res > label.resolution) label.resolution = res;
      this.labelGrow.set(p.id, grow);
      label.scale.set(size / zoom);
      label.position.set(anchor.x * ART, anchor.y * ART);
    }

    for (const [id, label] of this.labelPool) {
      if (shown.has(id)) continue;
      label.visible = false;
      // a kingdom that has fallen is not coming back; do not keep its Text alive
      if (!state.players.has(id)) {
        label.destroy();
        this.labelPool.delete(id);
        this.labelGrow.delete(id);
      }
    }
  }

  /**
   * **What every building on the map actually is.**
   *
   * Twenty-six buildings drawn at sixteen pixels are not tellable apart, and
   * squinting at a skyline to work out whether that is a Rune Hall or a Moon
   * Grove is not a decision, it is an eye test. So each one says its own name.
   *
   * Only past a zoom where they are separable — below that the names would
   * overlap into a grey smear and cost a hundred text updates a second to draw
   * it. The offset is in **world** units so the label follows the sprite as it
   * grows, while the glyphs themselves stay a constant size on screen.
   */
  private drawStructLabels(zoom: number) {
    let index = 0;
    if (zoom >= STRUCT_LABEL_ZOOM) {
      for (const s of state.structures.values()) {
        if (index >= 140) break;
        const def = STRUCTURE_DEFS[s.type];
        if (!def || def.buildable === false) continue;   // monster lairs name themselves
        let label = this.structLabelPool[index];
        if (!label) {
          label = new Text({
            text: '',
            style: {
              fontFamily: 'monospace', fontSize: 9, fill: 0xffffff, align: 'center',
              stroke: { color: 0x0a0d14, width: 3 },
            },
          });
          label.anchor.set(0.5, 0);
          label.resolution = 2;
          this.labels.addChild(label);
          this.structLabelPool[index] = label;
        }
        const owner = state.players.get(s.owner);
        label.visible = true;
        label.text = s.level > 1 ? `${def.name} ${s.level}` : def.name;
        label.tint = owner ? owner.color : 0xd8dde8;
        label.alpha = 0.92;
        label.scale.set(1 / zoom);
        label.position.set(((s.tile % MAP_W) + 0.5) * ART, (((s.tile / MAP_W) | 0) + 0.5) * ART + ART * 1.5);
        index++;
      }
    }
    /*
     * And a caption on every barrage in flight, sharing the building pool —
     * "Caster → Victim", so the map answers who did this to whom without
     * anybody having to have been watching the notice feed at the right moment.
     */
    for (const run of state.powers) {
      if (index >= this.structLabelPool.length + 8) break;
      const caster = state.players.get(run.owner);
      const victim = run.realm !== 0 ? state.players.get(run.realm) : null;
      let label = this.structLabelPool[index];
      if (!label) {
        label = new Text({
          text: '',
          style: {
            fontFamily: 'monospace', fontSize: 11, fill: 0xffffff, align: 'center',
            stroke: { color: 0x0a0d14, width: 4 },
          },
        });
        label.anchor.set(0.5, 1);
        label.resolution = 2;
        this.labels.addChild(label);
        this.structLabelPool[index] = label;
      }
      const def = RACE_POWERS[caster ? caster.race : 0] || RACE_POWERS[0];
      label.visible = true;
      label.text = victim
        ? `${def.name}\n${caster ? caster.name : '?'} → ${victim.name}`
        : `${def.name}\n${caster ? caster.name : '?'}`;
      label.tint = caster ? caster.color : 0xffffff;
      label.alpha = 1;
      label.scale.set(1 / zoom);
      label.position.set(run.x * ART, (run.y - run.area) * ART);
      index++;
    }

    for (let i = index; i < this.structLabelPool.length; i++) this.structLabelPool[i].visible = false;
  }

  /** Contested borders: attacking troops on the left, defenders on the right. */
  private drawFronts(zoom: number) {
    let index = 0;

    /*
     * How many men are in each of your transports, written under the hull.
     * Without it a loaded ship and an empty one look identical, and deciding
     * where to land is guesswork — which is the one decision the whole naval
     * game is about. Only your own and your allies' boats carry the number; the
     * server does not even send anyone else's.
     */
    for (const u of state.units.values()) {
      if (u.type !== UnitType.Boat || u.cargo <= 0) continue;
      const pair = this.unitPairs.get(u.id);
      if (!pair) continue;
      const label = this.frontLabel(index++);
      label.visible = true;
      const text = shortNumber(u.cargo);
      if (label.text !== text) label.text = text;
      label.tint = state.players.get(u.owner)?.color ?? 0xffffff;
      label.scale.set(0.72 / zoom);
      label.position.set(pair.base.x, pair.base.y + 7);
    }

    for (const f of this.fronts.values()) {
      const atk = state.players.get(f.attacker);
      const def = state.players.get(f.defender);
      const x = f.x * ART, y = f.y * ART;

      // only assign .text when it really changed: this runs every frame, and
      // every assignment re-rasterises the glyphs
      const left = this.frontLabel(index++);
      left.visible = true;
      const lt = shortNumber(Math.round(f.atkTroops));
      if (left.text !== lt) left.text = lt;
      left.tint = atk ? atk.color : 0xffffff;
      left.scale.set(1 / zoom);
      left.position.set(x - 17, y - 9);

      const right = this.frontLabel(index++);
      right.visible = true;
      const rt = shortNumber(Math.round(f.defTroops));
      if (right.text !== rt) right.text = rt;
      right.tint = def ? def.color : 0x9aa4b2;
      right.scale.set(1 / zoom);
      right.position.set(x + 17, y - 9);
    }
    for (let i = index; i < this.frontPool.length; i++) this.frontPool[i].visible = false;
  }

  private frontLabel(index: number): Text {
    let label = this.frontPool[index];
    if (!label) {
      // deliberately larger than any other label on the map: during a war this
      // is the number the whole decision hangs on
      label = new Text({
        text: '',
        style: {
          fontFamily: 'monospace', fontSize: 17, fontWeight: 'bold', fill: 0xffffff, align: 'center',
          stroke: { color: 0x0a0d14, width: 5 },
        },
      });
      label.anchor.set(0.5);
      label.resolution = 2;
      this.labels.addChild(label);
      this.frontPool[index] = label;
    }
    return label;
  }
}
